import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Input, Row } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { toPairs } from 'ramda';
import { Settings } from 'CoreApi.Settings_V12';
import { onlyDigits } from 'utils/validations';
import { commonFormCssMixin } from './Form';
import { renderValidationMarkCol } from '../utils';

interface BaseProps {
  settings: Settings | undefined;
  saveSettings: (values: any) => void;
  showPrevious?: boolean;
  goBack?: () => void;
  children?: React.ReactNode;
  formName: string;
  getValidations: (s: any) => Record<string, boolean>;
  additionalFields?: React.ReactNode;
}

const BaseInformationForm: React.FC<BaseProps> = ({
  settings,
  saveSettings,
  children,
  showPrevious = false,
  goBack,
  formName,
  getValidations,
  additionalFields,
}) => {
  const [form] = Form.useForm();
  const intl = useIntl();

  const [validations, setValidations] = useState(getValidations({}));

  const isFormValidated = useMemo(
    () => Object.values(validations).every((e) => e),
    [validations]
  );

  useEffect(() => {
    if (
      settings &&
      (formName === 'billing_address'
        ? settings?.billing_address !== null
        : settings?.shipping_address !== null)
    ) {
      const fieldsToSet = [
        ...toPairs(
          formName === 'billing_address'
            ? settings?.billing_address
            : settings?.shipping_address
        ).map((f) => ({
          name: [formName, f[0]],
          value: f[1],
        })),
        {
          name: 'country',
          value: settings?.country,
        },
      ];

      if (formName === 'billing_address') {
        fieldsToSet.push(
          {
            name: 'tax_number',
            value: settings?.tax_number ?? null,
          },
          {
            name: 'vat_number',
            value: settings?.vat_number ?? null,
          },
          {
            name: 'store_name',
            value: settings?.store_name ?? null,
          }
        );
      }

      form.setFields(fieldsToSet);
      const newValidations = getValidations(form.getFieldsValue());
      setValidations(newValidations);
    }
  }, [form, settings, formName, getValidations]);

  return (
    <Form
      form={form}
      colon={false}
      layout="vertical"
      onFinish={saveSettings}
      labelCol={{ span: 24 }}
      css={`
        ${() => commonFormCssMixin};
        margin: 0 auto;
      `}
      onValuesChange={(_, values) => {
        setValidations(getValidations(values));
      }}
    >
      <Row>
        <Col span={24}>
          <Row>
            <Col span={24}>{children}</Col>
          </Row>

          <Row css="margin: 30px 0 30px 14px">
            <Col span={22}>
              {formName === 'billing_address' ? (
                <FormattedMessage defaultMessage="Complete account and billing information" />
              ) : (
                <FormattedMessage defaultMessage="Complete store information" />
              )}
            </Col>
            {renderValidationMarkCol(isFormValidated, 4)}
          </Row>
          {formName === 'billing_address' && (
            <Row>
              <Col span="22">
                <Form.Item
                  name="store_name"
                  label={intl.formatMessage({ defaultMessage: 'Account name' })}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Account name',
                    })}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row css="padding-bottom: 5px; padding-left: 14px">
            {formName === 'billing_address' ? (
              <FormattedMessage defaultMessage="Billing address" />
            ) : (
              <FormattedMessage defaultMessage="Store addresss" />
            )}
          </Row>

          <Row>
            <Col span="11">
              <Form.Item name="country" noStyle>
                <></>
              </Form.Item>
              <Form.Item name={[formName, 'first_name']}>
                <Input
                  placeholder={intl.formatMessage({
                    defaultMessage: 'First name',
                  })}
                />
              </Form.Item>
            </Col>
            <Col span="11">
              <Form.Item name={[formName, 'last_name']}>
                <Input
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Last name',
                  })}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span="22">
              <Form.Item
                name={[formName, 'company']}
                rules={[
                  {
                    min: 3,
                    message: intl.formatMessage({
                      defaultMessage:
                        'The company name must be at least 3 characters long',
                    }),
                  },
                ]}
              >
                <Input
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Company name',
                  })}
                />
              </Form.Item>
            </Col>
            {renderValidationMarkCol(validations.companyNameValidated, 7)}
          </Row>
          <Row>
            <Col span="22">
              <Form.Item name={[formName, 'address1']}>
                <Input
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Street & Number',
                  })}
                />
              </Form.Item>
            </Col>
            {renderValidationMarkCol(validations.streetAddressValidated, 7)}
          </Row>
          <Row>
            <Col span="7">
              <Form.Item name={[formName, 'zipcode']}>
                <Input
                  onKeyPress={onlyDigits}
                  placeholder={intl.formatMessage({ defaultMessage: 'ZIP' })}
                />
              </Form.Item>
            </Col>
            <Col span="15">
              <Form.Item name={[formName, 'city']}>
                <Input
                  placeholder={intl.formatMessage({ defaultMessage: 'City' })}
                />
              </Form.Item>
            </Col>
            {renderValidationMarkCol(validations.cityValidated, 7)}
          </Row>
          <Row>
            <Col span="22">
              <Form.Item name={[formName, 'address2']}>
                <Input
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Additional information',
                  })}
                />
              </Form.Item>
              <Form.Item name="country" noStyle>
                <></>
              </Form.Item>
            </Col>
          </Row>

          {additionalFields}

          <Row justify="space-between" css="padding: 1em">
            {showPrevious && (
              <Button
                type="default"
                css="padding: 0 3em"
                onClick={() => {
                  if (goBack) goBack();
                }}
              >
                <FormattedMessage defaultMessage="Back to previous" />
              </Button>
            )}
            <Button
              type="primary"
              css="padding: 0 3em"
              onClick={() => form.submit()}
              disabled={!isFormValidated}
            >
              <FormattedMessage defaultMessage="Save and next" />
            </Button>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default BaseInformationForm;
